import { useHubble } from '@seek/hubble';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { isDev } from 'src/app/utils/environment';
import { getIsAdvertiserEmailUnverified } from 'src/app/utils/getIsAdvertiserEmailUnverified';
import { log } from 'src/app/utils/logging';
import { actionUpdateIsEmailVerified } from 'src/app/v1/state/modules/session/isEmailVerified';
import { actionUpdateUserId as actionUpdateSessionUserId } from 'src/app/v1/state/modules/session/userId';
import { actionUpdateEmailAddressVerified as actionUpdateIsEmailVerifiedTealium } from 'src/app/v1/state/modules/tealium/emailAddressVerified';
import { actionUpdateUserId as actionUpdateTealiumUserId } from 'src/app/v1/state/modules/tealium/userId';
import { useEmailVerificationSync } from 'src/app/v1/views/hooks/useEmailVerificationSync';
import { useIdentityLazyQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useIdentity = () => {
  const dispatch = useDispatch();
  const hubble = useHubble();
  const [loading, setLoading] = useState(true);
  const {
    advertiserId,
    flowType,
    isEmailUnverifiedInAdcentre,
    isDataCleanupComplete,
    userId,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      flowType: state.formData.flowType,
      isDataCleanupComplete: state.app.isDataCleanupComplete,
      isEmailUnverifiedInAdcentre: getIsAdvertiserEmailUnverified(
        state.session.hirerAccount.activationStatus,
      ),
      userId: state.session.userId,
    }),
    shallowEqual,
  );

  const { emailVerificationSync } = useEmailVerificationSync();

  const [getIdentityLazyQuery] = useIdentityLazyQuery({
    onCompleted: (data) => {
      const id = data?.identity.subject.userId;
      const isEmailVerifiedInSSO = Boolean(
        data?.identity.subject.emailVerified,
      );
      if (!id || Number(id) === 0) {
        log.error(
          `useIdentity | userID not found`,
          {
            id,
            advertiserId,
            flowType,
          },
          true,
        );
      }
      if (id) {
        dispatch(actionUpdateSessionUserId(id));
        dispatch(actionUpdateTealiumUserId(id));
        hubble.updateOptions({ loginId: userId });
      }

      dispatch(actionUpdateIsEmailVerified(isEmailVerifiedInSSO));
      dispatch(actionUpdateIsEmailVerifiedTealium(isEmailVerifiedInSSO));

      // If email is verified in SSO (from GraphQL identity.emailVerified)
      // but unverified in AdCentre (from GraphQL hirerAccount.status.PENDING_ACTIVATION)
      // Need to sync email verification back to AdCentre
      if (
        isEmailVerifiedInSSO &&
        isEmailUnverifiedInAdcentre &&
        flowType === 'create'
      ) {
        emailVerificationSync();
      }

      setLoading(false);
    },
    onError: (error) => {
      log.error('useIdentity | getIdentityLazyQuery error', { error });
      setLoading(false);
    },
    ...(isDev() && {
      context: {
        headers: { advertiserId },
      },
    }),
  });

  useEffect(() => {
    const asyncFn = async () => {
      setLoading(true);

      getIdentityLazyQuery();
    };

    if (isDataCleanupComplete && advertiserId) {
      asyncFn();
    }
  }, [advertiserId, getIdentityLazyQuery, isDataCleanupComplete]);

  return {
    loading,
  };
};
